import React, { useEffect, useId, useState } from 'react';
import Helmet from 'react-helmet';
import { success } from '../../util/Response';
import Link from '../detail-pages/Link';
import { Button } from '@/ui/components/button';
import { hasSignedInHint } from '../account/api';
import {
    SAVED_ITEM_IDS,
    SignedOutError,
    errorText,
    v2Session,
} from '../../components/lists/lists';
import { FOLLOW, UNFOLLOW } from '../profile/follows';
import {
    CREATE_POST,
    MAX_POST,
    FOLLOW_RESTAURANT,
    MY_FEED,
    MY_FOLLOWED_RESTAURANTS,
    RESTAURANT_FEED,
    UNFOLLOW_RESTAURANT,
    VIEWER,
    WHO_TO_FOLLOW,
    authorName,
    listCountText,
    type FeedAuthor,
    type FeedPost,
    type FeedRestaurant,
    type FollowSuggestion,
} from '../../components/feed/feed';
import { Avatar, PostCard, RestaurantAvatar, profilePath } from './PostCard';
import { StoriesStrip } from './stories';

/**
 * The feed (prototype MyFeed, /feed): what the diner and the people they follow
 * post, newest first, with reactions, comments, Report and Block. Routed only when
 * ENABLE_FEED is on at build time. Signed in only, so the server renders the shell
 * and the browser loads the posts from api-next; the page is never indexed. The
 * Restaurants-you-follow tab arrives with restaurant posts.
 */

export const fetchData = () => Promise.resolve(success({}));

type Viewer = FeedAuthor | null | undefined;

const Page = ({ children }: { children: React.ReactNode }) => (
    <div className="tw mx-auto w-full max-w-6xl px-4 py-6 md:px-8 md:py-10">
        <Helmet
            title="Your feed | NearBites"
            meta={[{ name: 'robots', content: 'noindex, nofollow' }]}
        />
        <h1 className="mb-4 text-title-1 font-semibold text-ink-strong">
            Your feed
        </h1>
        {children}
    </div>
);

const Notice = ({
    tone,
    children,
}: {
    tone?: 'error';
    children: React.ReactNode;
}) => (
    <p
        role={tone === 'error' ? 'alert' : undefined}
        className={`py-6 text-body-1 ${tone === 'error' ? 'text-error' : 'text-ink-muted'}`}
    >
        {children}
    </p>
);

function useViewer(): Viewer {
    const [viewer, setViewer] = useState<Viewer>(undefined);
    useEffect(() => {
        if (!hasSignedInHint()) {
            setViewer(null);
            return;
        }
        let cancelled = false;
        v2Session<{ viewer: FeedAuthor | null }>(VIEWER)
            .then(d => !cancelled && setViewer(d.viewer))
            .catch(() => !cancelled && setViewer(null));
        return () => {
            cancelled = true;
        };
    }, []);
    return viewer;
}

/** "What are you eating today?" (prototype MyFeed composer). */
const Composer = ({
    viewer,
    onPosted,
}: {
    viewer: FeedAuthor;
    onPosted: (post: FeedPost) => void;
}) => {
    const uid = useId();
    const [content, setContent] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        const text = content.trim();
        if (!text) return;
        setBusy(true);
        setError('');
        try {
            const { createPost } = await v2Session<{ createPost: FeedPost }>(
                CREATE_POST,
                {
                    content: text,
                },
            );
            onPosted(createPost);
            setContent('');
        } catch (err) {
            setError(
                err instanceof SignedOutError
                    ? 'Your session has ended. Log in again to post.'
                    : errorText(err, "We couldn't share your post. Try again."),
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="border-b border-border-subtle pb-6">
            <div className="flex items-center gap-3">
                <Avatar author={viewer} />
                <label htmlFor={`${uid}-post`} className="sr-only">
                    What are you eating today?
                </label>
                <textarea
                    id={`${uid}-post`}
                    value={content}
                    rows={1}
                    maxLength={MAX_POST}
                    placeholder="What are you eating today?"
                    onChange={e => setContent(e.target.value)}
                    className="field-sizing-content max-h-60 min-h-12 min-w-0 flex-1 resize-none rounded-3xl bg-surface-subtle px-5 py-3 text-body-1 text-ink outline-none placeholder:text-ink-muted focus-visible:ring-2 focus-visible:ring-ring"
                />
                <Button
                    type="submit"
                    size="sm"
                    className="rounded-full"
                    disabled={busy || !content.trim()}
                >
                    Post
                </Button>
            </div>
            {content.length > MAX_POST - 100 && (
                <p className="mt-2 text-right text-caption text-ink-muted">
                    {MAX_POST - content.length} characters left
                </p>
            )}
            {error && (
                <p role="alert" className="mt-2 text-body-2 text-error">
                    {error}
                </p>
            )}
        </form>
    );
};

/** One "Who to follow" row's button: Follow, then Following (tap again to undo). */
const FollowToggle = ({
    username,
    name,
}: {
    username: string;
    name: string;
}) => {
    const [following, setFollowing] = useState(false);
    const [busy, setBusy] = useState(false);
    const toggle = async () => {
        setBusy(true);
        try {
            await v2Session(following ? UNFOLLOW : FOLLOW, { username });
            setFollowing(f => !f);
        } catch {
            // Left as it was; the button stays usable.
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            type="button"
            aria-pressed={following}
            aria-label={`${following ? 'Unfollow' : 'Follow'} ${name}`}
            onClick={toggle}
            disabled={busy}
            className={`inline-flex min-h-8 shrink-0 items-center rounded-full border px-4 text-body-2 font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${following ? 'border-border-strong text-ink-muted hover:bg-surface-subtle' : 'border-ink text-ink hover:bg-surface-subtle'}`}
        >
            {following ? 'Following' : 'Follow'}
        </button>
    );
};

/** "Who to follow" (diners followed by the people you follow, then the most followed). */
const WhoToFollow = () => {
    const [suggestions, setSuggestions] = useState<FollowSuggestion[]>([]);
    useEffect(() => {
        let cancelled = false;
        v2Session<{ whoToFollow: FollowSuggestion[] }>(WHO_TO_FOLLOW, {
            limit: 3,
        })
            .then(d => !cancelled && setSuggestions(d.whoToFollow))
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);
    if (!suggestions.length) return null;
    return (
        <section
            aria-labelledby="who-to-follow"
            className="mb-8 border-b border-border-subtle pb-8"
        >
            <h2
                id="who-to-follow"
                className="mb-4 text-title-3 font-semibold text-ink-strong"
            >
                Who to follow
            </h2>
            <ul className="flex flex-col gap-4">
                {suggestions.map(({ profile, publicListCount }) => (
                    <li
                        key={profile.username}
                        className="flex items-center gap-3"
                    >
                        <Avatar author={profile} />
                        <div className="min-w-0 flex-1">
                            <Link
                                to={profilePath(profile.username)}
                                className="block truncate text-body-1 font-semibold text-ink-strong no-underline hover:text-primary"
                            >
                                {authorName(profile)}
                            </Link>
                            <p className="truncate text-caption text-ink-muted">
                                @{profile.username}
                                {publicListCount > 0 &&
                                    ` · ${listCountText(publicListCount)}`}
                            </p>
                        </div>
                        <FollowToggle
                            username={profile.username}
                            name={authorName(profile)}
                        />
                    </li>
                ))}
            </ul>
        </section>
    );
};

/** "Restaurants you follow" (mock sidebar), with Following buttons to unfollow. */
const FollowedRestaurants = () => {
    const [restaurants, setRestaurants] = useState<FeedRestaurant[]>([]);
    const [unfollowed, setUnfollowed] = useState<Set<string>>(new Set());
    useEffect(() => {
        let cancelled = false;
        v2Session<{ myFollowedRestaurants: FeedRestaurant[] }>(
            MY_FOLLOWED_RESTAURANTS,
        )
            .then(d => !cancelled && setRestaurants(d.myFollowedRestaurants))
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);
    if (!restaurants.length) return null;
    const toggle = async (id: string) => {
        const following = !unfollowed.has(id);
        try {
            await v2Session(
                following ? UNFOLLOW_RESTAURANT : FOLLOW_RESTAURANT,
                {
                    id,
                },
            );
            setUnfollowed(prev => {
                const next = new Set(prev);
                if (following) next.add(id);
                else next.delete(id);
                return next;
            });
        } catch {
            // Left as it was.
        }
    };
    return (
        <section
            aria-labelledby="restaurants-you-follow"
            className="mb-8 border-b border-border-subtle pb-8"
        >
            <h2
                id="restaurants-you-follow"
                className="mb-4 text-title-3 font-semibold text-ink-strong"
            >
                Restaurants you follow
            </h2>
            <ul className="flex flex-col gap-4">
                {restaurants.map(r => {
                    const following = !unfollowed.has(r.legacyRestaurantId);
                    const name = r.name ?? 'A restaurant';
                    return (
                        <li
                            key={r.legacyRestaurantId}
                            className="flex items-center gap-3"
                        >
                            <RestaurantAvatar restaurant={r} />
                            <div className="min-w-0 flex-1">
                                {r.path ? (
                                    <Link
                                        to={r.path}
                                        className="block truncate text-body-1 font-semibold text-ink-strong no-underline hover:text-primary"
                                    >
                                        {name}
                                    </Link>
                                ) : (
                                    <span className="block truncate text-body-1 font-semibold text-ink-strong">
                                        {name}
                                    </span>
                                )}
                                {r.locality && (
                                    <p className="truncate text-caption text-ink-muted">
                                        {r.locality}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                aria-pressed={following}
                                aria-label={`${following ? 'Unfollow' : 'Follow'} ${name}`}
                                onClick={() => toggle(r.legacyRestaurantId)}
                                className={`inline-flex min-h-8 shrink-0 items-center rounded-full border px-4 text-body-2 font-medium outline-none transition-colors hover:bg-surface-subtle focus-visible:ring-2 focus-visible:ring-ring ${following ? 'border-border-strong text-ink-muted' : 'border-ink text-ink'}`}
                            >
                                {following ? 'Following' : 'Follow'}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

const Sidebar = () => (
    <aside className="hidden lg:block">
        {process.env.ENABLE_FOLLOWS && <WhoToFollow />}
        {process.env.ENABLE_FOLLOWS && <FollowedRestaurants />}
        <div className="rounded-lg bg-surface-subtle p-5">
            <p className="text-body-1 font-medium text-ink-strong">
                You look hungry.
            </p>
            <Link
                to="/meals/"
                className="text-body-2 text-ink-muted no-underline hover:text-primary"
            >
                Find meals, not just venues.
            </Link>
        </div>
    </aside>
);

/**
 * Of the dishes and restaurants on the page, those on the viewer's lists (one request per
 * page of posts, so each Save button needn't ask).
 */
function useSavedIds(posts: FeedPost[] | null): Set<string> | null {
    const [saved, setSaved] = useState<Set<string> | null>(null);
    const [asked] = useState(() => new Set<string>());
    useEffect(() => {
        if (!process.env.ENABLE_LISTS || !posts) return;
        const meals: string[] = [];
        const restaurants: string[] = [];
        for (const { item } of posts) {
            const id = item?.legacyMealId ?? item?.legacyRestaurantId;
            if (!item || !id || asked.has(id)) continue;
            asked.add(id);
            (item.legacyMealId ? meals : restaurants).push(id);
        }
        if (!meals.length && !restaurants.length) {
            setSaved(prev => prev ?? new Set());
            return;
        }
        v2Session<{ savedItemIds: string[] }>(SAVED_ITEM_IDS, {
            legacyMealIds: meals,
            legacyRestaurantIds: restaurants,
        })
            .then(d =>
                setSaved(prev => new Set([...(prev ?? []), ...d.savedItemIds])),
            )
            .catch(() => setSaved(prev => prev ?? new Set()));
    }, [posts, asked]);
    return saved;
}

type Tab = 'following' | 'restaurants';

const FEEDS = {
    following: { query: MY_FEED, field: 'myFeed' },
    restaurants: { query: RESTAURANT_FEED, field: 'restaurantFeed' },
} as const;

/** One tab's posts: Following (with the post box) or Restaurants you follow (with stories). */
const PostFeed = ({ viewer, tab }: { viewer: FeedAuthor; tab: Tab }) => {
    const [posts, setPosts] = useState<FeedPost[] | null>(null);
    const [cursor, setCursor] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const saved = useSavedIds(posts);

    const load = async (before: string | null) => {
        const { query, field } = FEEDS[tab];
        const data = await v2Session<
            Record<string, { posts: FeedPost[]; nextCursor: string | null }>
        >(query, { before });
        return data[field];
    };

    useEffect(() => {
        let cancelled = false;
        load(null)
            .then(feed => {
                if (cancelled) return;
                setPosts(feed.posts);
                setCursor(feed.nextCursor);
            })
            .catch(
                err =>
                    !cancelled &&
                    setError(
                        errorText(
                            err,
                            "We couldn't load your feed. Check your connection and try again.",
                        ),
                    ),
            );
        return () => {
            cancelled = true;
        };
    }, [tab]);

    const more = async () => {
        if (!cursor) return;
        setLoadingMore(true);
        try {
            const feed = await load(cursor);
            setPosts(prev => {
                const seen = new Set((prev ?? []).map(p => p.id));
                return [
                    ...(prev ?? []),
                    ...feed.posts.filter(p => !seen.has(p.id)),
                ];
            });
            setCursor(feed.nextCursor);
        } catch (err) {
            setError(errorText(err, "We couldn't load more posts."));
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <div className="min-w-0">
            {tab === 'following' ? (
                <Composer
                    viewer={viewer}
                    onPosted={post => setPosts(prev => [post, ...(prev ?? [])])}
                />
            ) : (
                <StoriesStrip />
            )}
            {posts === null && !error && <Notice>Loading your feed…</Notice>}
            {posts?.length === 0 && (
                <Notice>
                    {tab === 'following'
                        ? 'Nothing here yet. Post something, or follow diners from their profiles to see what they eat.'
                        : 'No restaurant posts yet. Follow restaurants from their pages to see their news and specials here.'}
                </Notice>
            )}
            {posts?.map(post => (
                <PostCard
                    key={post.id}
                    post={post}
                    viewer={viewer}
                    saved={Boolean(
                        post.item &&
                        saved?.has(
                            post.item.legacyMealId ??
                                post.item.legacyRestaurantId ??
                                '',
                        ),
                    )}
                    onChange={next =>
                        setPosts(prev =>
                            (prev ?? []).map(p =>
                                p.id === next.id ? next : p,
                            ),
                        )
                    }
                    onRemoved={() =>
                        setPosts(prev =>
                            (prev ?? []).filter(p => p.id !== post.id),
                        )
                    }
                    onBlocked={username =>
                        setPosts(prev =>
                            (prev ?? []).filter(
                                p =>
                                    p.author?.username.toLowerCase() !==
                                    username.toLowerCase(),
                            ),
                        )
                    }
                />
            ))}
            {error && <Notice tone="error">{error}</Notice>}
            {cursor && (
                <div className="flex justify-center pt-6">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={more}
                        disabled={loadingMore}
                    >
                        {loadingMore ? 'Loading…' : 'Show older posts'}
                    </Button>
                </div>
            )}
        </div>
    );
};

const TABS: { id: Tab; label: string }[] = [
    { id: 'following', label: 'Following' },
    { id: 'restaurants', label: 'Restaurants you follow' },
];

/** ?tab=restaurants opens the second tab (shareable, survives a reload). */
function useTab(): [Tab, (tab: Tab) => void] {
    const [tab, setTab] = useState<Tab>('following');
    useEffect(() => {
        if (
            new URLSearchParams(window.location.search).get('tab') ===
            'restaurants'
        )
            setTab('restaurants');
    }, []);
    const choose = (next: Tab) => {
        setTab(next);
        const url = next === 'restaurants' ? '/feed?tab=restaurants' : '/feed';
        window.history.replaceState(window.history.state, '', url);
    };
    return [tab, choose];
}

export const Feed = () => {
    const viewer = useViewer();
    const [tab, setTab] = useTab();
    if (viewer === undefined) {
        return (
            <Page>
                <Notice>Loading…</Notice>
            </Page>
        );
    }
    if (viewer === null) {
        return (
            <Page>
                <div className="py-12 text-center">
                    <p className="mb-4 text-body-1 text-ink-muted">
                        Log in to see what the people you follow are eating.
                    </p>
                    <Button asChild>
                        <a href="/login?next=%2Ffeed">Log in</a>
                    </Button>
                </div>
            </Page>
        );
    }
    return (
        <Page>
            <div
                role="tablist"
                aria-label="Feed"
                className="mb-6 flex gap-8 overflow-x-auto border-b border-border-subtle"
            >
                {TABS.map(t => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.id}
                        onClick={() => setTab(t.id)}
                        className={`-mb-px shrink-0 border-b-2 pb-3 text-subtitle-1 whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring ${tab === t.id ? 'border-ink-strong font-semibold text-ink-strong' : 'border-transparent text-ink-muted hover:text-ink'}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <div className="grid gap-12 lg:grid-cols-[minmax(0,640px)_1fr]">
                <PostFeed key={tab} viewer={viewer} tab={tab} />
                <Sidebar />
            </div>
        </Page>
    );
};
